import React, { useCallback, useEffect, useState } from "react";
import {
  ArrayField,
  ArrayInput,
  BooleanField,
  ChipField,
  Create,
  Datagrid,
  DatagridConfigurable,
  DateField,
  Edit,
  FormDataConsumer,
  FunctionField,
  Labeled,
  List,
  NumberField,
  NumberInput,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  SelectArrayInput,
  SelectField,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  TextInput,
  maxValue,
  minValue,
  number,
  required,
  useDataProvider,
  usePermissions,
  useShowController,
} from "react-admin";
import { useLocation } from "react-router-dom";
import { useTranslate } from "ra-core";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";

import { CheckedOnlyField } from "../components/checked_only_field";
import {
  createAdjustUnitPriceChoices,
  createIsDeletedChoices,
  createPublishStateChoices,
  createStateChoices,
} from "../components/choices";
import { CloneButton } from "../components/clone_button";
import { ExistsSpecialConditionsField } from "../components/exists_special_conditions_field";
import { GradePenaltyInput } from "../components/grade_penalty_input";
import { HiddenInput } from "../components/hidden";
import { OfferShowActions } from "../components/OfferActions";
import { OfferPublicationPeriodInput } from "../components/offer_publication_period_input";
import { StoragePeriodsField } from "../components/storage_periods_field";
import { RelationalSelectInput } from "../components/relational_select_input";
import { StoragePeriodSelectArrayInput } from "../components/storage_period_select_array_input";

import {
  highestTradingUnits,
  likeInteger,
  steppedStringPrice,
  lowestTradingUnits,
  notSameGrade,
  validGradePenaltyPrice,
  validPublicationPeriod,
  validPackagingFormDifference,
  validStoragePeriodBonusExcludingTaxInYen,
} from "../utils/offer_validator";
import { hasBonusExcludingTaxInYen, StoragePeriodPriceRecordWithId } from "../utils/price_calculation";
import { preventSendNull } from "../utils/api";

import {
  FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS,
  FETCH_PARAMS_FOR_THOUSAND_ITEMS,
  INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS,
} from "../utils/parameters";
import { PrefecturesSelectField } from "../components/prefectures_select_field";
import { BrandSelectInput } from "../components/brand_select_input";
import { Holiday } from "../infrastructure/types";
import { validateMaxLengthText } from "../utils/validator";
import { PackagingForm } from "../api/common";
import { OmitReferenceArrayField } from "../components/omit_reference_array_field";
import { ListActions } from "../components/list_actions";
import { UndeletableToolbar } from "../components/UndeletableToolbar";
import { commonStyles } from "../components/common_styles";
import { IndexLabelSimpleFormIterator } from "../components/IndexLabelSimpleFormIterator";
import {
  formatDisplayOfferUnitPrice,
  formatDisplayStoragePeriodBonus,
  getDisplayOfferUnitPriceValue,
  priceCalculationModeChoices,
} from "../utils/price_calculation";

type Region = {
  id: number;
  is_active: boolean;
  name: string;
  prefecture_id: number;
};

export type PublicationPeriod = {
  id: number;
  is_active: boolean;
  is_deleted: boolean;
  name: string;
  publish_end_date: string;
  publish_start_date: string;
};

const styles = {
  progressWrapper: {
    alignItems: "center",
    display: "flex",
    height: 480,
    justifyContent: "center",
  },
};

const OfferFilter = [
  <NumberInput key="id" source="id" min={1} />,
  <TextInput key="annum_name" source="annum_name" />,
  <TextInput key="prefecture_name" source="prefecture_name" />,
  <TextInput key="brand_name" source="brand_name" />,
  <TextInput key="grade_name" source="grade_name" />,
  <SelectInput key="can_adjust_unit_price" source="can_adjust_unit_price" choices={createAdjustUnitPriceChoices()} />,
  <SelectInput key="publish_status" source="publish_status" choices={createPublishStateChoices()} />,
  <TextInput key="offer_publication_period_name" source="offer_publication_period_name" />,
  <SelectInput
    key="is_deleted"
    source="is_deleted"
    choices={createIsDeletedChoices()}
    // react-adminでデフォルトフィルタの値を未選択にするとクラッシュする問題の回避
    alwaysOn
    parse={(value: any) => {
      if (value === "" || value == null) {
        return undefined;
      }
      return value;
    }}
    format={(value: any) => {
      if (value == null) {
        return "";
      }
      return value;
    }}
  />,
  <SelectInput
    key="is_active"
    source="is_active"
    choices={createStateChoices()}
    // react-adminでデフォルトフィルタの値を未選択にするとクラッシュする問題の回避
    alwaysOn
    parse={(value: any) => {
      if (value === "" || value == null) {
        return undefined;
      }
      return value;
    }}
    format={(value: any) => {
      if (value == null) {
        return "";
      }
      return value;
    }}
  />,
];

const omitKeysForCloneButton = [
  "created_at",
  "created_by_account",
  "id",
  "is_active",
  "is_deleted",
  "publish_end_date",
  "publish_start_date",
  "publish_status",
  "trading_units_closed",
  "trading_units_left",
  "trading_units_provisional",
  "updated_at",
  "updated_by_account",
  "version_number",
];

const transformSourceForCloneButton = (record: any) => ({
  ...record,
  unit_price_excluding_tax_in_yen: getDisplayOfferUnitPriceValue(record),
});

export const OfferList = (props: any) => {
  const { permissions } = usePermissions();
  const disableCopyButton = !["admin", "staff"].includes(permissions);

  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = useState(false);
  const [validValues, setValidValues] = useState<{ [key: string]: any }>({});

  useEffect(() => {
    let isCleanedUp = false;

    (async () => {
      if (!isCleanedUp) {
        setIsLoading(true);
      }

      const fetchedAnnums = await dataProvider.getList("annums", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedBrands = await dataProvider.getList("brands", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedPrefectures = await dataProvider.getList("prefectures", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedRegions = await dataProvider.getList("regions", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedPackagingForms = await dataProvider.getList("packaging_forms", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedStoragePeriods = await dataProvider.getList("storage_periods", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedSpecialConditions = await dataProvider.getList(
        "special_conditions",
        FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS
      );

      if (!isCleanedUp) {
        // fetchに失敗すると結果がundefeindになるため
        if (
          fetchedAnnums &&
          fetchedBrands &&
          fetchedPrefectures &&
          fetchedRegions &&
          fetchedPackagingForms &&
          fetchedStoragePeriods &&
          fetchedSpecialConditions
        ) {
          setValidValues({
            annum_id: fetchedAnnums.data,
            prefecture_ids: fetchedPrefectures.data,
            brand_id: fetchedBrands.data,
            region_id: fetchedRegions.data,
            packaging_form_ids: fetchedPackagingForms.data,
            storage_period_ids: fetchedStoragePeriods.data,
            special_condition_ids: fetchedSpecialConditions.data,
          });
        }

        setIsLoading(false);
      }
    })();

    return () => {
      isCleanedUp = true;
    };
  }, [dataProvider]);

  if (isLoading) {
    return (
      <Box sx={styles.progressWrapper}>
        <CircularProgress />
      </Box>
    );
  }
  const preferenceKey = "offers";

  return (
    <List
      {...props}
      actions={<ListActions filters={OfferFilter} hasCreate={!disableCopyButton} preferenceKey={preferenceKey} />}
      filters={OfferFilter}
      filterDefaultValues={{ is_deleted: false, is_active: true }}
      sort={{ field: "id", order: "DESC" }}
      exporter={false}
    >
      <DatagridConfigurable bulkActionButtons={false} preferenceKey={preferenceKey} rowClick="show">
        <CloneButton
          omitKeys={omitKeysForCloneButton}
          disabled={disableCopyButton}
          transformSource={transformSourceForCloneButton}
          validValues={validValues}
          label="ra.action.clone"
        />
        <TextField source="id" />
        <ReferenceField source="annum_id" reference="annums" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <PrefecturesSelectField source="prefecture_ids" reference="prefectures" />
        <ReferenceField source="region_id" reference="regions" link={false} sortable={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="brand_id" reference="brands" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="grade_id" reference="grades" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <FunctionField
          source="packaging_form_ids"
          render={() => <OmitReferenceArrayField source="packaging_form_ids" reference="packaging_forms" />}
          sortable={false}
        />
        <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} sortable={false} />
        <FunctionField source="unit_price" render={formatDisplayOfferUnitPrice} sortable={false} />
        <SelectField source="can_adjust_unit_price" choices={createAdjustUnitPriceChoices()} sortable={false} />
        <NumberField source="total_trading_units" sortable={false} />
        <NumberField source="trading_units_closed" sortable={false} />
        <StoragePeriodsField source="storage_period_ids" sortable={false} />
        <ExistsSpecialConditionsField source="special_condition_ids" sortable={false} />
        <SelectField source="publish_status" choices={createPublishStateChoices()} sortable={false} />
        <ReferenceField source="offer_publication_period_id" reference="offer_publication_periods" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <DateField source="publish_start_date" locales={"ja"} sortable={false} />
        <DateField source="publish_end_date" locales={"ja"} sortable={false} />
        <CheckedOnlyField source="is_deleted" sortable={false} />
        <SelectField source="is_active" choices={createStateChoices()} sortable={false} />
      </DatagridConfigurable>
    </List>
  );
};

export const OfferShow = (props: any) => {
  const { permissions } = usePermissions();
  const { record } = useShowController();

  if (!record) {
    return null;
  }

  return (
    <Show {...props} actions={<OfferShowActions listPath="/offers" permissions={permissions} />}>
      <SimpleShowLayout>
        <TextField source="id" />
        <ReferenceField source="annum_id" reference="annums" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceArrayField source="prefecture_ids" reference="prefectures">
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        <ReferenceField source="region_id" reference="regions" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="brand_id" reference="brands" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField source="grade_id" reference="grades" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <ReferenceArrayField source="packaging_form_ids" reference="packaging_forms">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false}>
              <TextField source="name" sortable={false} label="resources.offers.fields.packaging_form_ids.name" />
              <NumberField
                source="deal_amount"
                sortable={false}
                label="resources.offers.fields.packaging_form_ids.deal_amount"
              />
            </Datagrid>
          </Box>
        </ReferenceArrayField>
        <SelectField source="price_calculation_mode" choices={priceCalculationModeChoices} />
        <FunctionField source="unit_price" render={formatDisplayOfferUnitPrice} />
        <SelectField source="can_adjust_unit_price" choices={createAdjustUnitPriceChoices()} />
        <ArrayField source="grade_penalties">
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false} empty={null}>
              <ReferenceField source="grade_id" reference="grades" link={false}>
                <TextField source="name" />
              </ReferenceField>
              <NumberField source="penalty_in_yen" emptyText={"受け取らない"} />
            </Datagrid>
          </Box>
        </ArrayField>
        <NumberField source="total_trading_units" />
        <NumberField source="lowest_trading_units" />
        <NumberField source="highest_trading_units" />
        <NumberField source="trading_units_closed" />
        <NumberField source="trading_units_provisional" />
        <NumberField source="trading_units_left" />
        <ReferenceArrayField source="storage_period_ids" reference="storage_periods" linkType={false}>
          <Box sx={commonStyles.showPageInnerDatagrid}>
            <Datagrid bulkActionButtons={false}>
              <TextField label="resources.storage_periods.fields.name" source="name" />
              <NumberField
                label="resources.storage_periods.fields.storage_period_in_days"
                source="storage_period_in_days"
              />
              <FunctionField
                label="resources.storage_periods.fields.bonus_in_yen"
                render={(storagePeriodRecord: any) =>
                  formatDisplayStoragePeriodBonus(storagePeriodRecord, record.price_calculation_mode)
                }
              />
            </Datagrid>
          </Box>
        </ReferenceArrayField>
        <ReferenceArrayField
          source="special_condition_ids"
          reference="special_conditions"
          className="array-field-that-potential-empty"
        >
          <SingleFieldList linkType={false}>
            <ChipField source="name" />
          </SingleFieldList>
        </ReferenceArrayField>
        {record.special_condition_ids !== undefined && record.special_condition_ids.length !== 0 && (
          <TextField source="special_conditions_memo" component="pre" />
        )}
        <SelectField source="publish_status" choices={createPublishStateChoices()} />
        <ReferenceField source="offer_publication_period_id" reference="offer_publication_periods" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <DateField source="publish_start_date" locales={"ja"} />
        <DateField source="publish_end_date" locales={"ja"} />
        {record && record.is_deleted && <BooleanField source="is_deleted" />}
        <SelectField source="is_active" choices={createStateChoices()} />
        <TextField source="created_by_account.name" />
        <DateField source="created_at" locales={"ja"} showTime={true} />
        <TextField source="updated_by_account.name" />
        <DateField source="updated_at" locales={"ja"} showTime={true} />
      </SimpleShowLayout>
    </Show>
  );
};

const validateRequired = [required()];
const validateUnitPrice = [required(), number(), minValue(5000), maxValue(50000), likeInteger];
const validateLowestUnits = [number(), minValue(1), maxValue(999), lowestTradingUnits];
const validateHighestUnits = [number(), minValue(1), maxValue(999), highestTradingUnits];
const validateTotalUnits = [required(), number(), minValue(1), maxValue(999)];
const validateGradePenaltyGrade = [required(), notSameGrade];
const validateGradePenaltyPrice = [
  minValue(-5000),
  maxValue(0),
  likeInteger,
  steppedStringPrice,
  validGradePenaltyPrice,
];
const validateSpecialConditionsMemo = validateMaxLengthText(2048);

const createRegionChoices = (regions: Region[], prefectureIds: number[]) => {
  const _prefectureIds = prefectureIds ? prefectureIds : [];
  if (_prefectureIds.length !== 1) {
    return [];
  }

  const prefectureId = _prefectureIds[0];
  return regions
    .filter((region) => region.prefecture_id === prefectureId)
    .map((region) => ({ id: region.id, name: region.name }));
};

type ImmutableMasterItem = StoragePeriodPriceRecordWithId & {
  name: string;
  is_active: boolean;
};

const transform = (data: any) => {
  const { highest_trading_units, lowest_trading_units, total_trading_units } = data;
  return {
    ...data,
    highest_trading_units: highest_trading_units ? highest_trading_units : total_trading_units,
    lowest_trading_units: lowest_trading_units ? lowest_trading_units : 1,
  };
};

const initialOfferValues = {
  prefecture_ids: [],
  region_id: "",
  grade_penalties: [{ grade_id: "", penalty_in_yen: "" }],
  special_condition_ids: [],
  special_conditions_memo: "",
};

export const OfferCreate = (props: any) => {
  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [publicationPeriods, setPublicationPeriods] = useState<PublicationPeriod[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [initialStoragePeriodId, setInitialStoragePeriodId] = useState<number[]>([]);
  const [storagePeriods, setStoragePeriods] = useState<ImmutableMasterItem[]>([]);
  const [packagingForms, setPackagingForms] = useState<PackagingForm[]>([]);
  const translate = useTranslate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get("source");
  // 複製から遷移した場合は変更がなくても保存ボタンを押せるようにする
  const loadingClone = !!source;

  useEffect(() => {
    let isCleanedUp = false;

    (async () => {
      if (!isCleanedUp) {
        setIsLoading(true);
      }

      const fetchedRegions = await dataProvider.getList("regions", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedStoragePeriods = await dataProvider.getList("storage_periods", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedHolidays = await dataProvider.getList("holidays", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedPackagingForms = await dataProvider.getList("packaging_forms", FETCH_PARAMS_FOR_THOUSAND_ITEMS);
      const periodParams = {
        ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS,
        filter: {
          ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS.filter,
          is_deleted: false,
          is_selectable: true,
        },
      };
      const fetchedOfferPublicationPeriods = await dataProvider.getList("offer_publication_periods", periodParams);

      if (!isCleanedUp) {
        // fetchに失敗すると結果がundefeindになるため
        if (fetchedRegions && fetchedStoragePeriods && fetchedOfferPublicationPeriods && fetchedPackagingForms) {
          setRegions(fetchedRegions.data);
          setHolidays(fetchedHolidays.data);
          setStoragePeriods(fetchedStoragePeriods.data);
          setPackagingForms(fetchedPackagingForms.data);

          const ids = fetchedStoragePeriods.data
            .filter(
              (item: ImmutableMasterItem) =>
                item.is_active && item.name.startsWith("標準") && hasBonusExcludingTaxInYen(item)
            )
            .map((item: ImmutableMasterItem) => item.id);
          setInitialStoragePeriodId(ids);

          const items = fetchedOfferPublicationPeriods.data.filter(
            (item: PublicationPeriod) => item.is_active && !item.is_deleted
          );
          setPublicationPeriods(items);
        }

        setIsLoading(false);
      }
    })();

    return () => {
      isCleanedUp = true;
    };
  }, [dataProvider]);

  const onChangeStoragePeriods = useCallback(
    (ids: number[]) => {
      const nextIds = ids.concat(initialStoragePeriodId);
      return Array.from(new Set(nextIds));
    },
    [initialStoragePeriodId]
  );

  if (isLoading) {
    return (
      <Box sx={styles.progressWrapper}>
        <CircularProgress />
      </Box>
    );
  }

  const now = new Date();
  const validateOfferPublicationPeriod = [required(), validPublicationPeriod(publicationPeriods, now, holidays)];
  const validatePackagingForms = [required(), validPackagingFormDifference(packagingForms)];
  const validateStoragePeriods = [required(), validStoragePeriodBonusExcludingTaxInYen(storagePeriods)];
  return (
    <Create {...props} redirect="show">
      <SimpleForm
        defaultValues={initialOfferValues}
        toolbar={<UndeletableToolbar alwaysEnableSaveButton={loadingClone} transform={transform} />}
        mode="onTouched"
      >
        <HiddenInput source="is_active" value={true} />
        {loadingClone && (
          <FormDataConsumer>
            {(formProps: any) => {
              const { formData } = formProps;
              const message =
                formData.invalidKeys && formData.invalidKeys.length > 0
                  ? "複製の際に無効なデータをクリアしています"
                  : null;

              return (
                message && (
                  <Typography color="error" variant="body1">
                    <p>{message}</p>
                    <ul>
                      {formData.invalidKeys.map((key: string) => {
                        return <li key={key}>{translate(`resources.offers.fields.${key}`)}</li>;
                      })}
                    </ul>
                  </Typography>
                )
              );
            }}
          </FormDataConsumer>
        )}
        <OfferPublicationPeriodInput
          source="offer_publication_period_id"
          validate={validateOfferPublicationPeriod}
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        />
        <ReferenceInput source="annum_id" reference="annums" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
          <SelectInput optionText="name" validate={validateRequired} />
        </ReferenceInput>
        <ReferenceArrayInput source="prefecture_ids" reference="prefectures" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
          <SelectArrayInput
            optionText="name"
            label="resources.offers.fields.prefecture_ids"
            validate={validateRequired}
            helperText="ui.offers.prefecture_ids_helper_text"
          />
        </ReferenceArrayInput>
        <FormDataConsumer>
          {(formProps: any) => {
            const { formData, ...rest } = formProps;
            const choices = createRegionChoices(regions, formData.prefecture_ids);
            return (
              <RelationalSelectInput source="region_id" selectedId={formData.region_id} choices={choices} {...rest} />
            );
          }}
        </FormDataConsumer>
        <BrandSelectInput source="brand_id" reference="brands" validate={validateRequired} />
        <ReferenceInput source="grade_id" reference="grades" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
          <SelectInput optionText="name" validate={validateRequired} />
        </ReferenceInput>
        <ReferenceArrayInput
          source="packaging_form_ids"
          reference="packaging_forms"
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        >
          <SelectArrayInput
            optionText="name"
            label="resources.offers.fields.packaging_form_ids"
            helperText="ui.offers.packaging_form_ids_helper_text"
            validate={validatePackagingForms}
          />
        </ReferenceArrayInput>
        <NumberInput
          source="unit_price_excluding_tax_in_yen"
          helperText="ui.offers.unit_price_helper_text"
          validate={validateUnitPrice}
        />
        <ArrayInput source="grade_penalties">
          <IndexLabelSimpleFormIterator addButtonText={translate("ui.offers.grade_penalties_add_button_text")}>
            <ReferenceInput source="grade_id" reference="grades" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
              <SelectInput optionText="name" validate={validateGradePenaltyGrade} />
            </ReferenceInput>
            <GradePenaltyInput source="penalty_in_yen" defaultValue="" validate={validateGradePenaltyPrice} />
          </IndexLabelSimpleFormIterator>
        </ArrayInput>
        <SelectInput
          source="can_adjust_unit_price"
          choices={createAdjustUnitPriceChoices()}
          validate={validateRequired}
          helperText="ui.offers.can_adjust_unit_price_helper_text"
        />
        <NumberInput
          source="total_trading_units"
          helperText="ui.offers.total_trading_units_helper_text"
          validate={validateTotalUnits}
        />
        <NumberInput
          source="lowest_trading_units"
          helperText="ui.offers.lowest_trading_units_helper_text"
          validate={validateLowestUnits}
        />
        <NumberInput
          source="highest_trading_units"
          helperText="ui.offers.highest_trading_units_helper_text"
          validate={validateHighestUnits}
        />
        <ReferenceArrayInput
          source="storage_period_ids"
          reference="storage_periods"
          defaultValue={initialStoragePeriodId}
          parse={onChangeStoragePeriods}
          helperText="標準は選択から外すことができません"
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        >
          <StoragePeriodSelectArrayInput
            optionText="name"
            label="resources.offers.fields.storage_period_ids"
            validate={validateStoragePeriods}
            helperText="ui.offers.storage_period_ids_helper_text"
          />
        </ReferenceArrayInput>
        <ReferenceArrayInput
          source="special_condition_ids"
          reference="special_conditions"
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        >
          <SelectArrayInput
            optionText="name"
            label="resources.offers.fields.special_condition_ids"
            helperText="ui.offers.special_condition_ids_helper_text"
            fullWidth
          />
        </ReferenceArrayInput>
        <FormDataConsumer>
          {({ formData }: { formData: any }) => {
            if (!formData.special_condition_ids || formData.special_condition_ids.length === 0) {
              return null;
            }
            return (
              <TextInput
                source="special_conditions_memo"
                label="resources.offers.fields.special_conditions_memo"
                parse={preventSendNull}
                validate={validateSpecialConditionsMemo}
                fullWidth
                multiline
              />
            );
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  );
};

export const OfferEdit = (props: any) => {
  const dataProvider = useDataProvider();
  const [isLoading, setIsLoading] = useState(false);
  const [regions, setRegions] = useState<Region[]>([]);
  const [initialStoragePeriodId, setInitialStoragePeriodId] = useState<number[]>([]);
  const [publicationPeriods, setPublicationPeriods] = useState<PublicationPeriod[]>([]);
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [storagePeriods, setStoragePeriods] = useState<ImmutableMasterItem[]>([]);
  const [packagingForms, setPackagingForms] = useState<PackagingForm[]>([]);
  const translate = useTranslate();

  useEffect(() => {
    let isCleanedUp = false;

    (async () => {
      if (!isCleanedUp) {
        setIsLoading(true);
      }

      const fetchedRegions = await dataProvider.getList("regions", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedStoragePeriods = await dataProvider.getList("storage_periods", FETCH_PARAMS_FOR_THOUSAND_ITEMS);
      const fetchedHolidays = await dataProvider.getList("holidays", FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS);
      const fetchedPackagingForms = await dataProvider.getList("packaging_forms", FETCH_PARAMS_FOR_THOUSAND_ITEMS);

      const periodParams = {
        ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS,
        filter: {
          ...FETCH_PARAMS_FOR_ALL_ACTIVE_ITEMS.filter,
          is_deleted: false,
        },
      };
      const fetchedOfferPublicationPeriods = await dataProvider.getList("offer_publication_periods", periodParams);

      if (!isCleanedUp) {
        // fetchに失敗すると結果がundefeindになるため
        if (fetchedRegions && fetchedStoragePeriods && fetchedOfferPublicationPeriods && fetchedPackagingForms) {
          setRegions(fetchedRegions.data);
          setHolidays(fetchedHolidays.data);
          setStoragePeriods(fetchedStoragePeriods.data);
          setPackagingForms(fetchedPackagingForms.data);

          const ids = fetchedStoragePeriods.data
            .filter(
              (item: ImmutableMasterItem) =>
                item.is_active && item.name.startsWith("標準") && hasBonusExcludingTaxInYen(item)
            )
            .map((item: ImmutableMasterItem) => item.id);
          setInitialStoragePeriodId(ids);

          const items = fetchedOfferPublicationPeriods.data.filter(
            (item: PublicationPeriod) => item.is_active && !item.is_deleted
          );
          setPublicationPeriods(items);
        }

        setIsLoading(false);
      }
    })();

    return () => {
      isCleanedUp = true;
    };
  }, [dataProvider]);

  const onChangeStoragePeriods = useCallback(
    (ids: number[]) => {
      const nextIds = ids.concat(initialStoragePeriodId);
      return Array.from(new Set(nextIds));
    },
    [initialStoragePeriodId]
  );

  if (isLoading) {
    return (
      <Box sx={styles.progressWrapper}>
        <CircularProgress />
      </Box>
    );
  }

  const now = new Date();
  const validateOfferPublicationPeriod = [required(), validPublicationPeriod(publicationPeriods, now, holidays)];
  const validatePackagingForms = [required(), validPackagingFormDifference(packagingForms)];
  const validateStoragePeriods = [required(), validStoragePeriodBonusExcludingTaxInYen(storagePeriods)];

  // HACK: undoable=falseにしないとhooksのdataProviderがデータ取得でundefinedを返してエラーになる
  return (
    <Edit {...props} transform={transform} undoable={false} redirect="show" mutationMode="pessimistic">
      <SimpleForm defaultValues={initialOfferValues} mode="onTouched">
        <Labeled>
          <TextField source="id" />
        </Labeled>
        <Labeled>
          <SelectField source="is_active" choices={createStateChoices()} validate={validateRequired} />
        </Labeled>
        <HiddenInput source="version_number" />
        <OfferPublicationPeriodInput
          source="offer_publication_period_id"
          validate={validateOfferPublicationPeriod}
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        />
        <ReferenceInput source="annum_id" reference="annums" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
          <SelectInput optionText="name" validate={validateRequired} />
        </ReferenceInput>
        <ReferenceArrayInput source="prefecture_ids" reference="prefectures" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
          <SelectArrayInput
            optionText="name"
            label="resources.offers.fields.prefecture_ids"
            validate={validateRequired}
            helperText="ui.offers.prefecture_ids_helper_text"
          />
        </ReferenceArrayInput>
        <FormDataConsumer>
          {(formProps: any) => {
            const { formData, ...rest } = formProps;
            const choices = createRegionChoices(regions, formData.prefecture_ids);
            return (
              <RelationalSelectInput source="region_id" selectedId={formData.region_id} choices={choices} {...rest} />
            );
          }}
        </FormDataConsumer>
        <BrandSelectInput source="brand_id" reference="brands" validate={validateRequired} />
        <ReferenceInput source="grade_id" reference="grades" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
          <SelectInput optionText="name" validate={validateRequired} />
        </ReferenceInput>
        <ReferenceArrayInput
          source="packaging_form_ids"
          reference="packaging_forms"
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        >
          <SelectArrayInput
            optionText="name"
            label="resources.offers.fields.packaging_form_ids"
            helperText="ui.offers.packaging_form_ids_helper_text"
            validate={validatePackagingForms}
          />
        </ReferenceArrayInput>
        <NumberInput
          source="unit_price_excluding_tax_in_yen"
          helperText="ui.offers.unit_price_helper_text"
          validate={validateUnitPrice}
        />
        <ArrayInput source="grade_penalties">
          <IndexLabelSimpleFormIterator addButtonText={translate("ui.offers.grade_penalties_add_button_text")}>
            <ReferenceInput source="grade_id" reference="grades" {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}>
              <SelectInput optionText="name" validate={validateGradePenaltyGrade} />
            </ReferenceInput>
            <GradePenaltyInput source="penalty_in_yen" defaultValue="" validate={validateGradePenaltyPrice} />
          </IndexLabelSimpleFormIterator>
        </ArrayInput>
        <SelectInput
          source="can_adjust_unit_price"
          choices={createAdjustUnitPriceChoices()}
          validate={validateRequired}
          helperText="ui.offers.can_adjust_unit_price_helper_text"
        />
        <NumberInput
          source="total_trading_units"
          helperText="ui.offers.total_trading_units_helper_text"
          validate={validateTotalUnits}
        />
        <NumberInput
          source="lowest_trading_units"
          helperText="ui.offers.lowest_trading_units_helper_text"
          validate={validateLowestUnits}
        />
        <NumberInput
          source="highest_trading_units"
          helperText="ui.offers.highest_trading_units_helper_text"
          validate={validateHighestUnits}
        />
        <ReferenceArrayInput
          source="storage_period_ids"
          reference="storage_periods"
          parse={onChangeStoragePeriods}
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        >
          <StoragePeriodSelectArrayInput
            optionText="name"
            label="resources.offers.fields.storage_period_ids"
            helperText="ui.offers.storage_period_ids_helper_text"
            validate={validateStoragePeriods}
          />
        </ReferenceArrayInput>
        <ReferenceArrayInput
          source="special_condition_ids"
          reference="special_conditions"
          {...INPUT_PARAMS_FOR_ALL_ACTIVE_ITEMS}
        >
          <SelectArrayInput
            optionText="name"
            label="resources.offers.fields.special_condition_ids"
            helperText="ui.offers.special_condition_ids_helper_text"
            fullWidth
          />
        </ReferenceArrayInput>
        <FormDataConsumer>
          {({ formData }: { formData: any }) => {
            if (!formData.special_condition_ids || formData.special_condition_ids.length === 0) {
              return null;
            }
            return (
              <TextInput
                source="special_conditions_memo"
                label="resources.offers.fields.special_conditions_memo"
                parse={preventSendNull}
                validate={validateSpecialConditionsMemo}
                fullWidth
                multiline
              />
            );
          }}
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  );
};
